<template>
  <div>
    <ModalBase :show-modal="showModal" :auto-h="true" :narrow="true" @close-modal="close">
      <template v-slot:titlebar>
        <TitleBar variant-class="title-modal">
          <template v-slot:pretitle>
            <span v-if="showEditProductForm">{{ $gettext('EDIT') }}</span>
            <span v-else-if="showCreateProductForm">{{ $gettext('ADD') }}</span>
            <span v-else>{{ $gettext('IMPORT TO INVOICE') }}</span>
          </template>
          <template v-slot:title>
            <span v-if="showEditProductForm">{{ $gettext('Edit product') }}</span>
            <span v-else-if="showCreateProductForm">{{ $gettext('New product') }}</span>
            <span v-else>{{ $gettext('Select product') }}</span>
          </template>
        </TitleBar>
      </template>

      <template v-slot:maincontent>
        <div v-if="showCreateProductForm || showEditProductForm">
          <AddEditProduct
            ref="add-edit-product-form"
            :product="editingProduct"
            :allow-group-creation="true"
            @product-created="onProductCreated"
            @product-updated="onProductUpdated"
            @group-created="onGroupCreated"
          />
        </div>

        <div v-else>
          <b-form-input v-model="searchTerm" :placeholder="$gettext('Search product...')" class="mb-3" />

          <overlay-scrollbars
            v-if="!isLoading && groupedProducts.length"
            :options="scrollBarsOptions"
            class="product-picker-groups"
          >
            <div v-for="group in groupedProducts" :key="group.id" class="product-group-wrap">
              <div class="product-group-name">{{ group.name }}</div>

              <div class="product-picker-list">
                <div
                  v-for="product in group.products"
                  :key="product.id"
                  class="product-picker-item"
                  :class="{
                    'product-picker-item--disabled': isProductDisabled(product),
                    'product-picker-item--selected': Number(product.multiplier) > 0,
                  }"
                >
                  <div class="product-picker-item__content">
                    <div class="product-picker-item__main">
                      <span class="product-picker-item__name">{{ product.description }}</span>
                      <span
                        v-if="product.amount !== null && product.amount !== undefined"
                        class="product-picker-item__price"
                      >
                        {{ productPriceLabel(product) }}
                      </span>
                    </div>
                  </div>
                  <GekkoButton
                    :icon="'fa-solid fa-pen-to-square'"
                    variant="link"
                    class="text-blue"
                    size="sm"
                    @click="openEditProductForm(product)"
                  />
                  <span class="gk-input-light product-quantity-selector">
                    

                    <GekkoButton
                      :icon="'fa-solid fa-minus'"
                      variant="light"
                      class="text-brand"
                      :disabled="isProductDisabled(product) || Number(product.multiplier || 0) === 0"
                      @click="subtractQuantity(product)"
                    />

                    <b-form-input
                      v-model.number="product.multiplier"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="0"
                      :disabled="isProductDisabled(product)"
                      @input="onQuantityInput(product)"
                    />

                    <GekkoButton
                      :icon="'fa-solid fa-plus'"
                      variant="light"
                      class="text-brand"
                      :disabled="isProductDisabled(product)"
                      @click="addQuantity(product)"
                    />
                  </span>
                </div>
              </div>
            </div>
          </overlay-scrollbars>

          <p v-else-if="isLoading" class="text-muted mb-0">{{ $gettext('Loading products...') }}</p>
          <p v-else class="text-muted mb-0">{{ $gettext('No products found.') }}</p>
        </div>
      </template>

      <template v-slot:actions>
        <div
          v-if="showCreateProductForm || showEditProductForm"
          class="product-picker-actions product-picker-actions--form"
        >
          <GekkoButton :text="$gettext('Back')" variant="link" class="text-blue p-0" @click="cancelInlineCreate" />

          <GekkoButton
            :text="showEditProductForm ? $gettext('Save') : $gettext('Save and add to invoice')"
            class="position-relative"
            :loading="isWaitingForResponse"
            @click="$refs['add-edit-product-form'].save()"
          />
        </div>

        <div v-else class="product-picker-actions">
          <GekkoButton
            :icon="'fa-solid fa-plus'"
            :text="$gettext('Add product')"
            variant="link"
            class="text-blue p-0"
            @click="openCreateProductForm"
          />

          <GekkoButton
            :text="$gettext('Add to invoice')"
            :disabled="selectedProducts.length === 0"
            class="product-picker-add-btn"
            @click="emitSelectedProducts"
          />
        </div>
      </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import ProductService from '@/store/services/product.service'
import ProductGroupService from '@/store/services/productgroup.service.js'

import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import AddEditProduct from '@/components/products/AddEditProduct.vue'

export default {
  name: 'ProductPickerModal',
  components: {
    GekkoButton,
    ModalBase,
    TitleBar,
    AddEditProduct,
  },
  props: {
    initialSearch: {
      type: String,
      default: '',
    },
    invoiceLines: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      groups: [],
      groupOptions: [],
      products: [],
      searchTerm: this.initialSearch,
      showCreateProductForm: false,
      showEditProductForm: false,
      editingProduct: null,
      showModal: true,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
      },
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: 'main/getCurrentCompany',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    currentProductLines() {
      return this.invoiceLines.filter((line) => Object.prototype.hasOwnProperty.call(line, 'product'))
    },
    filteredProducts() {
      const query = (this.searchTerm || '').trim().toLowerCase()

      if (!query) {
        return this.products
      }

      return this.products.filter((product) => product.description?.toLowerCase().includes(query))
    },
    groupedProducts() {
      const groupsWithProducts = this.groups
        .map((group) => ({
          ...group,
          products: this.filteredProducts.filter((product) => product.group === group.id),
        }))
        .filter((group) => group.products.length > 0)

      const groupedIds = new Set(this.groups.map((group) => group.id))
      const uncategorizedProducts = this.filteredProducts.filter((product) => !groupedIds.has(product.group))

      if (uncategorizedProducts.length > 0) {
        groupsWithProducts.push({
          id: 'other',
          name: this.$gettext('Other'),
          products: uncategorizedProducts,
        })
      }

      return groupsWithProducts
    },
    selectedProducts() {
      return this.products
        .filter((product) => Number(product.multiplier) > 0)
        .map((product) => ({
          ...product,
          multiplier: Number(product.multiplier),
        }))
    },
    activeSelectedProductId() {
      return this.selectedProducts[0]?.id || null
    },
    selectedUnits() {
      return this.selectedProducts.reduce((sum, product) => sum + product.multiplier, 0)
    },
  },
  created() {
    this.resetCreateForm()
    this.retrieveGroupsAndProducts()
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    close() {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    resetCreateForm() {
      this.createItem = {
        amount: null,
        amount_is_gross: false,
        archived: false,
        company_profile: this.currentCompany?.id,
        description: this.searchTerm || null,
        group: null,
        total_inc_vat: false,
        vat_exempt: false,
      }

      if (!this.currentCompany.default_tax_rate_exempt) {
        this.createItem.tax_rate = this.currentCompany.default_tax_rate
      } else {
        this.createItem.tax_rate = 21
        this.createItem.vat_exempt = true
      }

      this.newGroup = { name: null }
      this.showCreateGroupInput = false
    },
    cancelInlineCreate() {
      this.showCreateProductForm = false
      this.showEditProductForm = false
      this.editingProduct = null
      this.resetCreateForm()
    },
    retrieveGroupsAndProducts() {
      this.isLoading = true

      Promise.all([
        ProductGroupService.list({ company_profile: this.currentCompany.id }),
        ProductService.list({ company_profile: this.currentCompany.id, archived: 'False' }),
      ])
        .then(([groupResponse, productResponse]) => {
          const sortedGroups = [...groupResponse.data].sort((a, b) => a.name.localeCompare(b.name))
          this.groups = [{ id: null, name: this.$gettext('No Group') }, ...sortedGroups]
          this.groupOptions = [{ id: null, name: '---' }, ...sortedGroups]

          this.products = [...productResponse.data]
            .sort((a, b) => a.description.localeCompare(b.description))
            .map((product) => {
              const existingLine = this.currentProductLines.find((line) => line.product === product.id)
              return {
                ...product,
                multiplier: existingLine ? Number(existingLine.multiplier) || 0 : 0,
              }
            })
        })
        .catch(() => {
          this.groups = []
          this.groupOptions = []
          this.products = []
        })
        .finally(() => {
          this.normalizeSelectedProducts()
          this.isLoading = false
          if (this.products.length === 0) {
            this.showCreateProductForm = true
          }
        })
    },
    normalizeSelectedProducts() {
      const firstSelectedProduct = this.products.find((product) => Number(product.multiplier) > 0)

      if (!firstSelectedProduct) {
        return
      }

      this.products.forEach((product) => {
        if (product.id !== firstSelectedProduct.id && Number(product.multiplier) > 0) {
          this.$set(product, 'multiplier', 0)
        }
      })
    },
    clearSelectedProducts() {
      this.products.forEach((product) => {
        if (Number(product.multiplier) > 0) {
          this.$set(product, 'multiplier', 0)
        }
      })
    },
    openCreateProductForm() {
      this.clearSelectedProducts()
      this.showCreateProductForm = true
      this.showEditProductForm = false
      this.editingProduct = null
    },
    openEditProductForm(product) {
      this.editingProduct = product
      this.showEditProductForm = true
      this.showCreateProductForm = false
    },
    onProductCreated(product) {
      this.$emit('product-created', product)
      this.$emit('products-selected', [{ ...product, multiplier: 1 }])
      this.retrieveGroupsAndProducts()
      this.cancelInlineCreate()
    },
    onProductUpdated(productId) {
      this.$emit('product-updated', productId)
      this.retrieveGroupsAndProducts()
      this.cancelInlineCreate()
    },
    onGroupCreated() {
      this.$emit('group-created')
      this.retrieveGroupsAndProducts()
    },
    isProductDisabled(product) {
      return this.activeSelectedProductId !== null && this.activeSelectedProductId !== product.id
    },
    setProductMultiplier(product, nextValue) {
      const parsedValue = Number(nextValue)
      const normalizedValue = Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : Math.floor(parsedValue)

      if (this.isProductDisabled(product)) {
        return
      }

      if (normalizedValue > 0) {
        this.products.forEach((currentProduct) => {
          if (currentProduct.id !== product.id && Number(currentProduct.multiplier) > 0) {
            this.$set(currentProduct, 'multiplier', 0)
          }
        })
      }

      this.$set(product, 'multiplier', normalizedValue)
    },
    addQuantity(product) {
      this.setProductMultiplier(product, Number(product.multiplier || 0) + 1)
    },
    subtractQuantity(product) {
      this.setProductMultiplier(product, Number(product.multiplier || 0) - 1)
    },
    onQuantityInput(product) {
      this.setProductMultiplier(product, product.multiplier)
    },
    emitSelectedProducts() {
      if (!this.selectedProducts.length) {
        return
      }

      this.$emit('products-selected', this.selectedProducts)

      this.close()
    },
    selectProduct(product) {
      if (this.isProductDisabled(product)) {
        return
      }

      this.setProductMultiplier(product, 1)
      this.emitSelectedProducts()
    },
    productPriceLabel(product) {
      if (product.amount === null || product.amount === undefined || product.amount === '') {
        return ''
      }
      const amount = parseFloat(product.amount)
      if (isNaN(amount)) return ''

      const multiplier = Number(product.multiplier) > 0 ? Number(product.multiplier) : 1

      const taxRate = parseFloat(product.tax_rate) || 0
      const currencyFilter = this.$options.filters.currencyString
      const preferredLanguage = this.$store.getters['main/getPreferredLanguage'] ?? 'en'

      let exVat, inclVat
      const unitExVat = Math.round(amount * 100) / 100
      const unitInclVat = taxRate > 0 ? Math.round(amount * (1 + taxRate / 100) * 100) / 100 : unitExVat
      exVat = Math.round(unitExVat * multiplier * 100) / 100
      inclVat = Math.round(unitInclVat * multiplier * 100) / 100

      const exVatStr = currencyFilter(exVat, 'EUR')
      const inclVatStr = currencyFilter(inclVat, 'EUR')

      if (product.vat_exempt) {
        return `${exVatStr} (${this.$gettext('VAT exempt')})`
      }

      const taxRateStr = taxRate.toLocaleString(preferredLanguage, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })

      if (product.amount_is_gross) {
        return `${inclVatStr} (${this.$gettext('inc')} ${taxRateStr}% VAT)`
      }

      return `${exVatStr} / ${inclVatStr} (${taxRateStr}% VAT)`
    },
  },
}
</script>

<style lang="scss" scoped>
.product-picker-groups {
  max-height: 340px;
  overflow-y: auto;
}

.product-group-wrap {
  margin-bottom: 1rem;
}

.product-group-name {
  font-size: 22px;
  font-family: DINNextLTPro-Medium, sans-serif;
  margin-bottom: 0.5rem;
}

.product-picker-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.product-picker-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  border-radius: 12px;
  border: 1px solid $colorBorderDash;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.product-picker-item--disabled {
  opacity: 0.55;
}

.product-picker-item--selected {
  background: #e7fdd3;
}

.product-picker-item__content {
  flex: 1;
  min-width: 0;
}

.product-picker-item__main {
  width: 100%;
  border: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.25rem;
  text-align: left;
}

.product-picker-item__name {
  color: $colorTextBase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-picker-item__price {
  color: $colorTextLight;
  font-size: 0.85em;
}

.product-quantity-selector {
  display: flex;
  gap: 0.5rem;
  align-items: center;

  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    height: 24px;
    width: 24px;
    border-radius: 100px;
    padding: 0;
    font-size: 12px;
  }

  input {
    width: 60px;
    text-align: center;
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.product-quantity-selector .form-control:disabled {
  background-color: white !important;
}

.product-picker-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 576px) {
    flex-direction: row;
  }
}

.product-picker-add-btn {
  width: 100%;

  @media (min-width: 576px) {
    width: auto;
  }
}
</style>
